import * as THREE from "three/webgpu";
import {BehaviorSubject, type Observable} from 'rxjs';
import {SketchCircleObject} from '../mesh/sketch';
import type {SketchPointObject} from '../mesh/sketch/SketchPointObject';
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';

import {buildCircleFromThreePoints2D} from '../tool/buildCircleFromThreePoints2D';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';
import {syncThinChainLineGeometry, ThinChainLIneNodeMaterial,} from '../material';


/** 圆预览颜色。 */
const CIRCLE_PREVIEW_COLOR = 0x53a9ff;


/** 圆预览透明度。 */
const CIRCLE_PREVIEW_OPACITY = 0.8;
/** 圆预览分段数。 */
const CIRCLE_PREVIEW_SEGMENT_COUNT = 96;
/** 圆心半径圆预览半径引导细点画线线段长度。 */
const CIRCLE_RADIUS_GUIDE_DASH_SIZE = 22;
/** 圆心半径圆预览半径引导细点画线间隔长度。 */
const CIRCLE_RADIUS_GUIDE_GAP_SIZE = 4;
/** 圆心半径圆预览半径引导细点画线点段长度。 */
const CIRCLE_RADIUS_GUIDE_DOT_SIZE = 4;

/**
 * 创建圆心半径圆预览半径引导细点画线材质。
 * 说明：半径引导线是绘制过程中的辅助提示，不属于最终圆实体，因此固定使用屏幕空间细点画线。
 * @param points 半径引导线点列。
 * @returns 屏幕空间细点画线材质。
 */
function createCircleRadiusGuideMaterial(points: THREE.Vector3[]): ThinChainLIneNodeMaterial {
    const material = new ThinChainLIneNodeMaterial({
        color: CIRCLE_PREVIEW_COLOR,
        linewidth: 1,
        dashSize: CIRCLE_RADIUS_GUIDE_DASH_SIZE,
        gapSize: CIRCLE_RADIUS_GUIDE_GAP_SIZE,
        dotSize: CIRCLE_RADIUS_GUIDE_DOT_SIZE,
        opacity: CIRCLE_PREVIEW_OPACITY,
        transparent: true,
        points,
    });
    Reflect.set(material, 'name', '圆心半径圆预览-屏幕空间细点画线半径引导材质');
    return material;
}



/**
 * 圆绘制命令枚举。
 */
export enum SketchCircleCommand {
    /** 圆心半径圆命令。 */
    CenterRadius = 'sketch-circle',
    /** 三点圆命令。 */
    ThreePoint = 'sketch-circle-3-point',
}

/**
 * 判断命令是否为圆绘制命令。
 * @param command 命令字符串。
 * @returns 是否为圆绘制命令。
 */
export function isSketchCircleCommand(command: string): command is SketchCircleCommand {
    return Object.values(SketchCircleCommand).includes(command as SketchCircleCommand);
}

/**
 * 圆绘制选项。
 */
export interface CircleOptions {
    /** 圆绘制模式。 */
    mode: SketchCircleCommand;
    /** 是否为构造几何。 */
    forConstruction: boolean;
}

/**
 * 圆绘制参数。
 */
export interface CircleParameters {
    /** 圆心坐标。 */
    center?: [number, number, number];
    /** 圆心2D坐标。 */
    center2d?: [number, number];
    /** 半径。 */
    radius?: number;
}


/**
 * 草图圆绘制处理器。
 * 说明：
 * 1. 完整承接旧版 circle 鼠标按下与实体创建流程；
 * 2. 坐标解析优先级：绘图平面拾取 -> 最近端点吸附；
 * 3. 达到模式所需点数后立即创建圆并清理交互状态。
 */
export class SketchCircleDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Circle;
    /** 圆工具选项。 */
    public circleOptions: CircleOptions = {
        mode: SketchCircleCommand.CenterRadius,
        forConstruction: false,
    };
    /** 圆参数。 */
    public circleParameters: CircleParameters | null = null;

    /** 每次圆绘制点击命中的来源点对象。 */
    private drawingPointTargets: Array<SketchPointObject | null> = [];

    /** 圆参数实时流（绘制预览时发射当前参数，绘制结束时发射 null）。 */
    private readonly circleParametersSubject: BehaviorSubject<CircleParameters | null> = new BehaviorSubject<CircleParameters | null>(null);

    /** 圆预览外轮廓对象缓存。 */
    private circlePreviewCircle: THREE.Line | null = null;
    /** 圆心半径圆预览半径引导线对象缓存。 */
    private circleRadiusGuidePreview: THREE.Line | null = null;
    /** 圆预览外轮廓当前是否为构造线样式。 */
    private circlePreviewIsConstruction: boolean | null = null;
    /** 圆预览圆心局部坐标复用对象。 */
    private readonly circlePreviewLocalCenter = new THREE.Vector2();
    /** 圆心半径圆预览边缘点局部坐标复用对象。 */
    private readonly circleRadiusGuideLocalEnd = new THREE.Vector2();
    /** 圆预览外轮廓采样点复用数组。 */
    private readonly circlePreviewPoints = Array.from(
        {length: CIRCLE_PREVIEW_SEGMENT_COUNT + 1},
        () => new THREE.Vector3(),
    );
    /** 圆心半径圆预览半径引导线采样点复用数组。 */
    private readonly circleRadiusGuidePoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];


    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    /**
     * 观测圆参数实时变化流。
     * @returns 可观察的圆参数流，绘制中发射 CircleParameters，绘制结束发射 null。
     */
    public observeCircleParameters(): Observable<CircleParameters | null> {
        return this.circleParametersSubject.asObservable();
    }

    
    getRequiredPointCount(): number {
        return this.circleOptions.mode === SketchCircleCommand.ThreePoint ? 3 : 2;
    }

    
    get closable(): boolean {
        return false;
    }

    
    resetState(): void {
        this.drawingPoints = [];

        this.drawingPointTargets = [];


        this.clearCirclePreviewCache();

        this.circleParameters = null;
        this.circleParametersSubject.next(null);
    }


    /**
     * 获取圆工具绘制提示。
     * @returns 提示文本。
     */
    getDrawingHint(): string {
        let baseHint = '';
        const pointCount = this.drawingPoints.length;
        if (this.circleOptions.mode === SketchCircleCommand.CenterRadius) {
            baseHint = pointCount === 0
                ? '步骤 1/2：点击圆心'
                : '步骤 2/2：点击边缘点';
        } else if (pointCount === 0) {
            baseHint = '步骤 1/3：点击圆上第一个点';
        } else if (pointCount === 1) {
            baseHint = '步骤 2/3：点击圆上第二个点';
        } else {
            baseHint = '步骤 3/3：点击圆上第三个点';
        }

        let extraInfo = '';
        if (pointCount > 0 && this.sketchDrawingManager.getPreviewPoint()) {
            if (this.circleOptions.mode === SketchCircleCommand.CenterRadius) {
                const radius = this.drawingPoints[0].distanceTo(this.sketchDrawingManager.getPreviewPoint());
                extraInfo += ` - 半径: ${radius.toFixed(2)}`;
            } else if (pointCount >= 2) {
                const point1 = this.referencePlane.worldToLocal(this.drawingPoints[0]);
                const point2 = this.referencePlane.worldToLocal(this.drawingPoints[1]);
                const point3 = this.referencePlane.worldToLocal(this.sketchDrawingManager.getPreviewPoint());
                const circleData = buildCircleFromThreePoints2D(point1, point2, point3);
                if (circleData) {
                    extraInfo += ` - 半径: ${circleData.radius.toFixed(2)}`;
                }
            }
        }

        return `${baseHint}${extraInfo} | ESC取消`;
    }


    /**
     * 统一预览更新入口。

     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        const firstPoint = this.drawingPoints[0];
        if (!firstPoint){
            return;
        }
        if (this.circleOptions.mode === SketchCircleCommand.CenterRadius) {
            this.createCenterRadiusCirclePreviewInHandler(firstPoint, currentPoint);
            return;
        }
        if (this.drawingPoints.length === 1) {
            this.createDiameterCirclePreviewInHandler(firstPoint, currentPoint);
            return;
        }
        this.createThreePointCirclePreviewInHandler(
            this.drawingPoints[0],
            this.drawingPoints[1],
            currentPoint,
        );
    }

    /**
     * 处理圆绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。

     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {

        // 与线绘制保持一致：上游已解析出点捕捉结果时，必须优先使用捕捉点，
        // 避免圆心仍落到鼠标原始按下位置，导致与 SolidWorks 行为不一致。
        const resolvedPosition = this.resolveCircleDrawingPosition(payload);

        this.drawingPoints.push(resolvedPosition.clone());

        // 圆实体创建阶段需要知道每次点击是否命中正式草图点，以便新控制点记录来源。
        this.drawingPointTargets.push(payload.snappedTargetPoint ?? null);

        const requiredPointCount = this.getRequiredPointCount();
        if (this.drawingPoints.length !== requiredPointCount) {
            return;
        }

        this.createCircleEntity();

        this.cleanupDrawingState();

        this.drawingPointTargets = [];

        // 创建完成后清除绘制预览状态。
        this.circleParametersSubject.next(null);
    }


    /**
     * 获取圆绘制所需点数。
     * @param mode 圆模式。
     * @returns 所需点数。
     */
    public getCirclePointCount(mode: CircleOptions['mode']): number {
        return mode === SketchCircleCommand.ThreePoint ? 3 : 2;
    }


    /**
     * 更新圆参数到 circleParameters 属性。
     * @param centerWorld 圆心世界坐标。
     * @param radius 半径。
     * @param centerLocalOverride 局部坐标覆盖值。
     */
    public updateCircleParameters(
        centerWorld: THREE.Vector3,
        radius: number,
        centerLocalOverride?: THREE.Vector2
    ): void {
        const round = (value: number) => Number(value.toFixed(8));
        const resolvedCenterLocal = centerLocalOverride
            ?? (() => {
                const localPoint = this.referencePlane.worldToLocal(centerWorld);
                return new THREE.Vector2(localPoint.x, localPoint.y);
            })();
        this.circleParameters = {
            center: centerWorld.toArray() as [number, number, number],
            center2d: [round(resolvedCenterLocal.x), round(resolvedCenterLocal.y)],
            radius: round(radius),
        };
        this.circleParametersSubject.next(this.circleParameters);
    }

    /**
     * 在处理器内创建“圆心-半径”预览。

     * @param center 圆心。
     * @param edge 边缘点。
     */
    private createCenterRadiusCirclePreviewInHandler(
        center: THREE.Vector3,
        edge: THREE.Vector3,
    ): void {
        const radius = center.distanceTo(edge);
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.destroyPreview();
            return;
        }
        this.renderCirclePreviewInHandler(center, radius, this.circleOptions.forConstruction, edge);
        this.updateCircleParameters(center, radius, this.referencePlane.worldToLocal(center));
    }

    /**
     * 在处理器内创建“直径”预览。

     * @param pointA 直径端点 A。
     * @param pointB 直径端点 B。
     */
    private createDiameterCirclePreviewInHandler(
        pointA: THREE.Vector3,
        pointB: THREE.Vector3,
    ): void {
        const center = pointA.clone().add(pointB).multiplyScalar(0.5);
        const radius = pointA.distanceTo(pointB) * 0.5;
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.destroyPreview();
            return;
        }
        this.renderCirclePreviewInHandler(center, radius, this.circleOptions.forConstruction);
        this.updateCircleParameters(center, radius, this.referencePlane.worldToLocal(center));
    }

    /**
     * 在处理器内创建“三点圆”预览。

     * @param point1 点 1。
     * @param point2 点 2。
     * @param point3 点 3。
     */
    private createThreePointCirclePreviewInHandler(
        point1: THREE.Vector3,
        point2: THREE.Vector3,
        point3: THREE.Vector3,
    ): void {
        const local1 = this.referencePlane.worldToLocal(point1);
        const local2 = this.referencePlane.worldToLocal(point2);
        const local3 = this.referencePlane.worldToLocal(point3);
        const circleData = buildCircleFromThreePoints2D(local1, local2, local3);
        if (!circleData) {
            this.destroyPreview();
            return;
        }
        const center = this.referencePlane.localToWorld(new THREE.Vector2(circleData.center.x, circleData.center.y));
        this.renderCirclePreviewInHandler(center, circleData.radius, this.circleOptions.forConstruction);
        this.updateCircleParameters(center, circleData.radius, circleData.center);
    }

    /**
     * 在处理器内渲染圆形预览线。
     * @param center 圆心。
     * @param radius 半径。
     * @param isConstruction 是否构造线。
     * @param radiusGuideEnd 半径引导终点；仅圆心半径圆预览传入。
     */
    private renderCirclePreviewInHandler(
        center: THREE.Vector3,
        radius: number,
        isConstruction: boolean,
        radiusGuideEnd: THREE.Vector3 | null = null,
    ): void {
        const points = this.updateCirclePreviewPoints(center, radius);
        const hasRadiusGuide = radiusGuideEnd !== null;
        const radiusGuidePoints = hasRadiusGuide
            ? this.updateCircleRadiusGuidePoints(center, radiusGuideEnd)
            : null;

        if (!this.isCirclePreviewActive(hasRadiusGuide, isConstruction)) {
            this.destroyPreview();
            this.sketchDrawingManager.previewCircle = this.createCirclePreviewLine(points, isConstruction);
            this.circlePreviewCircle = this.sketchDrawingManager.previewCircle;
            this.circlePreviewIsConstruction = isConstruction;
            this.viewer.addObject(this.sketchDrawingManager.previewCircle);

            if (radiusGuidePoints) {
                this.sketchDrawingManager.previewLine = this.createCircleRadiusGuideLine(radiusGuidePoints);
                this.circleRadiusGuidePreview = this.sketchDrawingManager.previewLine;
                this.viewer.addObject(this.sketchDrawingManager.previewLine);
            }
            return;
        }

        this.updateCirclePreviewLine(this.circlePreviewCircle as THREE.Line, points, isConstruction);
        if (radiusGuidePoints && this.circleRadiusGuidePreview) {
            this.updateCircleRadiusGuideLine(this.circleRadiusGuidePreview, radiusGuidePoints);
        }
    }


    /**
     * 销毁当前绘制预览并清空圆预览缓存引用。
     * 说明：圆预览复用对象；当进入其他绘制阶段或清理状态时，缓存必须同步失效。
     */
    protected override destroyPreview(): void {
        super.destroyPreview();
        this.clearCirclePreviewCache();
    }

    /**
     * 清空圆预览缓存引用。
     */
    private clearCirclePreviewCache(): void {
        this.circlePreviewCircle = null;
        this.circleRadiusGuidePreview = null;
        this.circlePreviewIsConstruction = null;
    }

    /**
     * 判断当前圆预览缓存是否仍可复用。
     * @param hasRadiusGuide 本次预览是否需要半径引导线。
     * @param isConstruction 本次预览是否为构造线样式。
     * @returns 是否可复用当前预览对象。
     */
    private isCirclePreviewActive(hasRadiusGuide: boolean, isConstruction: boolean): boolean {
        const isCircleActive = Boolean(
            this.circlePreviewCircle
            && this.sketchDrawingManager.previewCircle === this.circlePreviewCircle
            && this.circlePreviewIsConstruction === isConstruction,
        );
        const isGuideActive = hasRadiusGuide
            ? Boolean(
                this.circleRadiusGuidePreview
                && this.sketchDrawingManager.previewLine === this.circleRadiusGuidePreview,
            )
            : this.circleRadiusGuidePreview === null
                && this.sketchDrawingManager.previewLine === null;

        return isCircleActive && isGuideActive;
    }

    /**
     * 原地更新圆预览外轮廓采样点。
     * @param center 圆心世界坐标。
     * @param radius 圆半径。
     * @returns 可写入预览几何的复用采样点数组。
     */
    private updateCirclePreviewPoints(center: THREE.Vector3, radius: number): THREE.Vector3[] {
        const localCenter = this.referencePlane.worldToLocalTo(center, this.circlePreviewLocalCenter);
        for (let index = 0; index <= CIRCLE_PREVIEW_SEGMENT_COUNT; index += 1) {
            const angle = (Math.PI * 2 * index) / CIRCLE_PREVIEW_SEGMENT_COUNT;
            const x = localCenter.x + radius * Math.cos(angle);
            const y = localCenter.y + radius * Math.sin(angle);
            // 预览高频更新时复用采样点，避免鼠标移动时反复分配整圈点集。
            this.referencePlane.localToWorldTo(x, y, this.circlePreviewPoints[index]);
        }

        return this.circlePreviewPoints;
    }

    /**
     * 原地更新圆心半径圆预览半径引导线采样点。
     * @param center 圆心世界坐标。
     * @param radiusGuideEnd 半径引导终点世界坐标。
     * @returns 可写入预览几何的复用采样点数组。
     */
    private updateCircleRadiusGuidePoints(
        center: THREE.Vector3,
        radiusGuideEnd: THREE.Vector3,
    ): THREE.Vector3[] {
        this.circleRadiusGuidePoints[0].copy(center);
        const localEnd = this.referencePlane.worldToLocalTo(radiusGuideEnd, this.circleRadiusGuideLocalEnd);
        this.referencePlane.localToWorldTo(localEnd, this.circleRadiusGuidePoints[1]);

        return this.circleRadiusGuidePoints;
    }

    /**
     * 创建圆预览外轮廓。
     * @param points 圆外轮廓采样点。
     * @param isConstruction 是否构造线。
     * @returns 圆预览外轮廓对象。
     */
    private createCirclePreviewLine(points: THREE.Vector3[], isConstruction: boolean): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = isConstruction
            ? new THREE.LineDashedMaterial({
                color: CIRCLE_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: CIRCLE_PREVIEW_OPACITY,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: CIRCLE_PREVIEW_COLOR,
                linewidth: 2,
                opacity: CIRCLE_PREVIEW_OPACITY,
                transparent: true,
            });
        const previewCircle = new THREE.Line(geometry, material);
        if (material instanceof THREE.LineDashedMaterial) {
            previewCircle.computeLineDistances();
        }

        return previewCircle;
    }

    /**
     * 创建圆心半径圆预览半径引导线。
     * @param points 半径引导线点列。
     * @returns 半径引导线对象。
     */
    private createCircleRadiusGuideLine(points: THREE.Vector3[]): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const guideLine = new THREE.Line(geometry, createCircleRadiusGuideMaterial(points));
        syncThinChainLineGeometry(guideLine);
        return guideLine;
    }

    /**
     * 更新圆预览外轮廓几何。
     * @param line 圆预览外轮廓对象。
     * @param points 新采样点。
     * @param isConstruction 是否构造线。
     */
    private updateCirclePreviewLine(
        line: THREE.Line,
        points: THREE.Vector3[],
        isConstruction: boolean,
    ): void {
        this.updatePreviewLineGeometry(line, points);
        if (isConstruction) {
            line.computeLineDistances();
        }
    }

    /**
     * 更新圆心半径圆预览半径引导线几何。
     * @param line 半径引导线对象。
     * @param points 新采样点。
     */
    private updateCircleRadiusGuideLine(line: THREE.Line, points: THREE.Vector3[]): void {
        this.updatePreviewLineGeometry(line, points);
        syncThinChainLineGeometry(line);
    }

    /**
     * 原地更新预览线几何。
     * @param line 待更新的预览线。
     * @param points 新点列。
     */
    private updatePreviewLineGeometry(line: THREE.Line, points: THREE.Vector3[]): void {
        const positionAttribute = line.geometry.getAttribute('position');
        if (positionAttribute instanceof THREE.BufferAttribute && positionAttribute.count === points.length) {
            points.forEach((point, index) => {
                positionAttribute.setXYZ(index, point.x, point.y, point.z);
            });
            positionAttribute.needsUpdate = true;
        } else {
            line.geometry.setFromPoints(points);
        }

        line.geometry.computeBoundingSphere();
    }


    /**
     * 创建圆实体并同步参数与轮廓。
     */
    private createCircleEntity(): void {
        let center: THREE.Vector3 | null = null;
        let radius = 0;
        let localCenter: THREE.Vector2 | null = null;

        if (this.circleOptions.mode === SketchCircleCommand.CenterRadius) {
            if (this.drawingPoints.length < 2) {
                return;
            }
            center = this.drawingPoints[0];
            const edgePoint = this.drawingPoints[1];
            radius = center.distanceTo(edgePoint);
            const localCenterVec = this.referencePlane.worldToLocal(center);
            localCenter = new THREE.Vector2(localCenterVec.x, localCenterVec.y);
        } else {
            if (this.drawingPoints.length < 3) {
                return;
            }
            const local1 = this.referencePlane.worldToLocal(this.drawingPoints[0]);
            const local2 = this.referencePlane.worldToLocal(this.drawingPoints[1]);
            const local3 = this.referencePlane.worldToLocal(this.drawingPoints[2]);
            const circleData = buildCircleFromThreePoints2D(local1, local2, local3);
            if (!circleData) {
                return;
            }
            localCenter = circleData.center;
            radius = circleData.radius;
            center = this.referencePlane.localToWorld(new THREE.Vector2(localCenter.x, localCenter.y));
        }

        if (!center || !localCenter) {
            return;
        }

        const isConstruction = this.circleOptions.forConstruction;
        const material = this.createSketchLineMaterial(isConstruction, false);

        const edgeControlPointPosition = localCenter
            ? this.referencePlane.localToWorld(new THREE.Vector2(localCenter.x + radius, localCenter.y))
            : null;
        const centerControlPoint = this.circleOptions.mode === SketchCircleCommand.CenterRadius
            ? this.resolveReusableCircleControlPoint(0, center)
            : null;
        const edgeControlPoint = this.circleOptions.mode === SketchCircleCommand.CenterRadius
            ? this.resolveReusableCircleControlPoint(1, edgeControlPointPosition)
            : null;
        const centerSourcePoint = this.circleOptions.mode === SketchCircleCommand.CenterRadius
            && !centerControlPoint
            ? this.resolveCircleControlPointSource(0, center)
            : null;
        const edgeSourcePoint = this.circleOptions.mode === SketchCircleCommand.CenterRadius
            && !edgeControlPoint
            ? this.resolveCircleControlPointSource(1, edgeControlPointPosition)
            : null;


        const circle = new SketchCircleObject({
            center,
            radius,
            centerPoint: centerControlPoint ?? undefined,
            edgePoint: edgeControlPoint ?? undefined,
            centerSourcePoint,
            edgeSourcePoint,
            material,
            referencePlane: this.referencePlane,
            isConstruction,
        });


        circle.updateGeometry();

        if (this.viewer.shadedSketchContoursEnabled) {
            circle.syncContour();
        }

        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(circle);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(circle.getCenterControlPoint());
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(circle.getEdgeControlPoint());
        this.viewer.addSketchObject(circle);
        this.sketchDrawingManager.notifySketchEntityCreated(circle);

        const sketchGroupManager = this.viewer.sketchGroupManager;
        if (!sketchGroupManager) {
            throw new Error('草图组管理器未初始化，无法同步草图轮廓显示状态。');
        }
        this.viewer.syncShadedContoursAfterSketchEntityCreated(sketchGroupManager.activeSketchGroup);

        this.updateCircleParameters(center, radius, localCenter);

        // 创建完成后程序化选中新圆，使面板参数可编辑。
        this.viewer.entitySelectionManager?.selectEntityProgrammatically(circle);
    }

    /**
     * 解析圆绘制当前点击点。
     * 说明：
     * 1. 优先使用绘制管理器预先注入的 snappedPoint；
     * 2. 无捕捉结果时再回退到鼠标原始落面点；
     * 3. 统一在此方法收口圆绘制点击点来源，避免输入优先级分散。
     * @param payload 鼠标按下载荷。
     * @returns 本次点击最终采用的世界坐标。
     */
    private resolveCircleDrawingPosition(payload: SketchDrawingMouseDownPayload): THREE.Vector3 {
        return payload.snappedPoint ?? this.getDrawingPoint(payload.event);
    }


    /**
     * 解析圆绘制中可记录为来源的捕捉点对象。
     *
     * 说明：
     * 1. 只有捕捉目标点与最终实体控制点位置仍在闭合容差内时才记录来源；
     * 2. 三点圆的圆心由三点反算，不直接对应某一次点击，因此当前仅圆心半径圆记录点击来源；
     * 3. 来源引用不表示拓扑共点，圆心或半径控制点仍是新建点对象。
     *
     * @param pointIndex 绘制点击索引。
     * @param expectedPoint 最终控制点位置。
     * @returns 可记录的来源点对象；不可记录时返回 null。
     */
    private resolveCircleControlPointSource(
        pointIndex: number,
        expectedPoint: THREE.Vector3 | null | undefined,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex] ?? null;
        return this.resolveControlPointSource(targetPoint, expectedPoint);
    }

    /**
     * 解析圆绘制中是否命中了可直接复用的线段端点。
     * @param pointIndex 绘制点击索引。
     * @param expectedPoint 最终控制点位置。
     * @returns 可复用的线段端点；否则返回 null。
     */
    private resolveReusableCircleControlPoint(
        pointIndex: number,
        expectedPoint: THREE.Vector3 | null | undefined,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex] ?? null;
        return this.resolveReusableLineEndpointControlPoint(targetPoint, expectedPoint);
    }

}


